import { SelectionModel } from '@angular/cdk/collections';
import { AsyncPipe, NgClass, NgIf, NgStyle } from '@angular/common';
import {
  AfterViewInit,
  Component,
  ElementRef,
  Inject,
  OnDestroy,
  OnInit,
  ViewChild,
  ViewEncapsulation,
} from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { MatDividerModule } from '@angular/material/divider';
import { MatIconModule } from '@angular/material/icon';
import { MatLegacyButtonModule as MatButtonModule } from '@angular/material/legacy-button';
import { MatLegacyDialogModule as MatDialogModule } from '@angular/material/legacy-dialog';
import { MatLegacyFormFieldModule as MatFormFiledModule } from '@angular/material/legacy-form-field';
import { MatLegacyInputModule as MatInputModule } from '@angular/material/legacy-input';
import { MatLegacySnackBarModule, MatLegacySnackBar as MatSnackBar } from '@angular/material/legacy-snack-bar';
import { MatLegacyTableModule as MatTableModule } from '@angular/material/legacy-table';
import { MatLegacyTooltipModule as MatTooltipModule } from '@angular/material/legacy-tooltip';
import { MatTableDataSource } from '@angular/material/table';
import { MatToolbarModule } from '@angular/material/toolbar';
import { OrderDetailService } from '@cutting/main/order/order-detail/order-detail.service';
import {
  BazisMaterialHelper,
  CuttingMaterial,
  CuttingMaterialBaseDialogComponent,
  CuttingMaterialBaseDialogData,
  CuttingSettings,
  ICustomResponse,
  ImageViewerComponent,
  ImageViewerData,
  LegacyDialogsService,
  MaterialForReplacementToElement,
  NoDataDisplayComponent,
} from 'cloud-shared-lib';
import { Observable, Subject, fromEvent } from 'rxjs';
import { debounceTime, distinctUntilChanged, takeUntil, tap } from 'rxjs/operators';

export interface RecommendedMaterialsDialogData {
  elementId: number;
  productId: number;
  cuttingOptions: CuttingSettings;
  priceListId: number;
}

@Component({
  templateUrl: './recomended-materials-dialog.component.html',
  styleUrls: ['./recomended-materials-dialog.component.scss'],
  standalone: true,
  imports: [
    NgIf,
    NgClass,
    NgStyle,
    AsyncPipe,
    MatToolbarModule,
    MatTableModule,
    MatButtonModule,
    MatDialogModule,
    MatTooltipModule,
    MatIconModule,
    MatDividerModule,
    NoDataDisplayComponent,
    MatFormFiledModule,
    MatInputModule,
    MatLegacySnackBarModule,
  ],
  encapsulation: ViewEncapsulation.None,
})
export class RecommendedMaterialsDialogComponent implements OnInit, OnDestroy, AfterViewInit {
  @ViewChild('filter') filter: ElementRef;
  displayColumns = ['preview', 'name', 'article'];
  dataSource: MatTableDataSource<MaterialForReplacementToElement> = new MatTableDataSource();
  selection: SelectionModel<MaterialForReplacementToElement> = new SelectionModel(false, []);

  data$: Observable<ICustomResponse<MaterialForReplacementToElement[]>>;
  unsubscribe$: Subject<void> = new Subject();

  get showQuantityInStock(): boolean {
    return this.data.cuttingOptions.showQuantityInStock;
  }

  constructor(
    private legacyDialogsService: LegacyDialogsService,
    private orderDetailService: OrderDetailService,
    public dialogRef: MatDialogRef<RecommendedMaterialsDialogComponent, MaterialForReplacementToElement[]>,
    private snackBar: MatSnackBar,
    @Inject(MAT_DIALOG_DATA) public data: RecommendedMaterialsDialogData
  ) {}

  ngOnInit(): void {
    this.data$ = this.orderDetailService.getPiceListElementMaterials(this.data.productId, this.data.elementId).pipe(
      tap(m => {
        this.dataSource.data = m.result || [];
      })
    );
  }

  ngAfterViewInit(): void {
    this.filterListener();
  }

  /**
   * The label for the checkbox on the passed row
   * @param row
   */
  checkboxLabel(row: MaterialForReplacementToElement): string {
    return `${this.selection.isSelected(row) ? 'deselect' : 'select'} row ${row.materialName}`;
  }

  openMaterialBaseDialog(): void {
    this.legacyDialogsService
      .component(
        CuttingMaterialBaseDialogComponent,
        {
          materialsToExclude: [],
          multiple: false,
          showQuantityInStock: this.showQuantityInStock,
        } as CuttingMaterialBaseDialogData,
        1150,
        {
          panelClass: 'material-base-material-list-dialog',
        }
      )
      .subscribe((result: CuttingMaterial[] | undefined) => {
        if (result && result.length > 0) {
          const mbMaterial = result[0];
          const material = new MaterialForReplacementToElement();
          material.materialName = mbMaterial.name;
          material.article = mbMaterial.article;
          material.materialId = mbMaterial.id;
          material.textureName = mbMaterial.textureName;
          material.cropTextureLink = mbMaterial.cropTextureLink;
          material.textureLink = mbMaterial.textureLink;
          material.price = 0;

          this.dialogRef.close([material]);
        }
      });
  }

  openTextureViewerDialog(link: string): void {
    this.legacyDialogsService
      .component(
        ImageViewerComponent,
        {
          textureLink: link,
        } as ImageViewerData,
        undefined,
        {
          panelClass: 'image-viewer-dialog',
        }
      )
      .subscribe();
  }

  extractMaterialName(materialName: string): any {
    return BazisMaterialHelper.extractMaterialName(materialName);
  }

  extractMaterialCode(materialName: string): any {
    return BazisMaterialHelper.extractMaterialCode(materialName);
  }

  ngOnDestroy(): void {
    this.unsubscribe$.next();
  }

  private filterListener(): void {
    fromEvent(this.filter.nativeElement, 'keyup')
      .pipe(
        debounceTime(300),
        distinctUntilChanged(),
        tap(() => this.applyFilter()),
        takeUntil(this.unsubscribe$)
      )
      .subscribe();
  }

  private applyFilter(): void {
    const filterValue = this.filter.nativeElement.value;
    this.dataSource.filter = filterValue.trim().toLowerCase();
  }
}
